import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import { CloudOff } from 'lucide-react';

/**
 * OfflinePill — el indicador de "sin conexión" del CHASIS (F6.7b post-smoke).
 *
 * Antes vivía dentro del Panel (`dashboard:offline_chip`, en la fila del
 * saludo). El smoke on-device dejó claro el problema: el usuario sale del Panel
 * y pierde de vista la única señal de que está sin conexión, así que las cosas
 * deshabilitadas en otras pantallas se sienten rotas en vez de explicadas. El
 * indicador pertenece al chasis, no a una pantalla.
 *
 * Se monta DENTRO del <header> fijo de AppLayout, al lado del menú de usuario.
 * Eso es lo que le da, gratis y sin una sola regla propia:
 *   - la safe area de arriba (el header ya lleva `pt-[env(safe-area-inset-top)]`);
 *   - imposibilidad de tapar contenido: el <main> ya empieza por debajo del
 *     header, y el pill no añade altura (el header tiene alto fijo);
 *   - imposibilidad de tapar el bottom nav: está en el extremo opuesto.
 * CERO safe areas escritas a mano, cero position:fixed nuevo.
 *
 * Gobernado por `useNetworkStatus`: aparece con el DICTAMEN de offline y
 * desaparece SOLO al volver la señal, en cualquier pantalla y sin navegar.
 * App-only por construcción — el vigía no dictamina en web (networkVerdict es
 * app-only), así que `isOffline` allá es false para siempre y esto devuelve
 * null: la web no estrena indicador.
 *
 * `shrink-0` + `whitespace-nowrap`: en un viewport angosto el pill no se parte
 * ni empuja al menú de usuario fuera de la fila (mismo criterio que traía el
 * chip del Panel).
 */
// sqlite-811 (offline toque, firma de Carlos del 28-oct): cuanto dura abierta la burbuja.
export const BURBUJA_MS = 4000;
// El aire minimo entre la burbuja y los bordes de la pantalla.
const MARGEN_PX = 12;

export default function OfflinePill({ conTexto = true }) {
  const { isOffline } = useNetworkStatus();
  const { t, ready } = useReadyTranslation(['common']);
  const [abierta, setAbierta] = useState(false);
  const [corrimiento, setCorrimiento] = useState(0);
  const caja = useRef(null);
  const burbujaRef = useRef(null);

  // Medido en Chrome: anclada al borde izquierdo de la pastilla, en 320 y 393 de ancho la burbuja se salia por la
  // derecha (y anclada al borde derecho se salia por la izquierda). Al abrirse se recorre a la izquierda lo justo para
  // quedar a 12 px del borde derecho de la pantalla, sin pasar del margen de 12 px del izquierdo.
  useLayoutEffect(() => {
    if (!abierta) { setCorrimiento(0); return; }
    const el = burbujaRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const sobra = r.right - (window.innerWidth - MARGEN_PX);
    if (sobra > 0) setCorrimiento(Math.min(sobra, Math.max(0, r.left - MARGEN_PX)));
  }, [abierta]);

  // La burbuja se cierra sola a los 4 s, al tocar fuera de la pastilla y de la burbuja, y con Escape. Un segundo
  // toque en la pastilla la cierra (el clic de abajo), no reinicia el reloj.
  useEffect(() => {
    if (!abierta) return undefined;
    const reloj = setTimeout(() => setAbierta(false), BURBUJA_MS);
    const alTocar = (e) => {
      if (caja.current && !caja.current.contains(e.target)) setAbierta(false);
    };
    const alTeclear = (e) => {
      if (e.key === 'Escape') setAbierta(false);
    };
    document.addEventListener('pointerdown', alTocar);
    document.addEventListener('keydown', alTeclear);
    return () => {
      clearTimeout(reloj);
      document.removeEventListener('pointerdown', alTocar);
      document.removeEventListener('keydown', alTeclear);
    };
  }, [abierta]);

  // Al volver (o al perder) la red, la burbuja empieza cerrada: sin red otra vez no reaparece abierta.
  useEffect(() => { setAbierta(false); }, [isOffline]);

  if (!isOffline || !ready) return null;
  // dashboard-rediseno (G4): a la izquierda de los creditos, con la nube tachada. En el telefono (abajo de 640) solo
  // el icono, con su nombre como etiqueta accesible; desde 640, icono y texto. El texto es el de hoy.
  // sqlite-811: la pastilla es un boton (mismo aspecto); el nombre accesible sigue en la etiqueta y la burbuja
  // sustituye al titulo del navegador, para que no salgan dos cosas a la vez en la computadora.
  const nombre = t('common:offline.mode_chip');
  return (
    <span ref={caja} className="relative inline-flex shrink-0">
      <button
        type="button"
        aria-label={nombre}
        aria-expanded={abierta}
        onClick={() => setAbierta((a) => !a)}
        className="shrink-0 inline-flex items-center gap-1.5 h-8 whitespace-nowrap rounded-full border border-[#2C2C2C] bg-[#1B1B1B] px-2.5 text-[12px] font-medium text-[#C8C8C8] cursor-pointer"
        data-testid="offline-pill"
      >
        <CloudOff className="w-3.5 h-3.5 shrink-0 text-[#9A9A9A]" aria-hidden="true" />
        {/* Vuelta 2 (D4): la barra apaga el texto cuando no cabe; el nombre sigue como etiqueta accesible. */}
        {conTexto && <span className="hidden sm:inline" aria-hidden="true">{nombre}</span>}
      </button>
      {abierta && (
        <div
          ref={burbujaRef}
          role="tooltip"
          data-testid="offline-burbuja"
          style={corrimiento ? { left: `${-corrimiento}px` } : undefined}
          className="absolute left-0 top-10 z-50 w-max max-w-[280px] rounded-xl border border-[#2C2C2C] bg-[#1B1B1B] px-3 py-2.5 text-[13px] leading-snug text-[#E6E6E6] shadow-[0_10px_30px_rgba(0,0,0,0.55)]"
        >
          {t('common:offline.tap_hint')}
        </div>
      )}
    </span>
  );
}
